import React, { memo, useState, useEffect } from 'react';
import { array, bool, func, string } from 'prop-types';
import styled from 'styled-components';

import Collapsable from 'library/components/collapsable';
import CarrotToggle from 'library/components/carrot-toggle';
import MultiSelectOption from 'library/components/form-components/dropdown/components/multi-select-option';
import useWithA11y from 'library/hooks/use-with-a11y';
import KeyCodes from 'library/helpers/key-codes';
import 'library/helpers/array';

const StyledCollapsableRow = styled.div`
  display: flex;
  flex-direction: column;
  transition: all 0.125s ease-in-out;

  .collapsable {
    transition: height 0.2s ease-in-out;
    will-change: height;
    overflow: hidden;
  }

  > &:first-child {
    flex: 1;
  }

  &:hover {
    background: #9090901a;
  }
`;

const StyledDropdownContent = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0.5em;
  border-bottom: 1px solid lightgrey;
`;

const StyledParent = styled.div`
  cursor: pointer;
  margin: 0 1em;
  transition: all 0.125s ease-in-out;
  display: flex;
  ${(props) => (props.isTabbing ? `` : `outline: none;`)}
  align-items: center;
  border-top: 1px solid lightgrey;

  ${(props) =>
    props.collapsed
      ? `
  border-bottom: 1px solid lightgrey;
  `
      : ``}
  > :first-child {
    flex: 1;
  }

  > :last-child {
    padding: 1em 0.5em;
  }

  > * {
    ${(props) => (props.isTabbing ? `` : `outline: none;`)}
  }
`;

const NestedMultiSelectOption = ({
  aggregateValue,
  label,
  onClick,
  onKeyPress,
  value,
  nestedOptions,
  name,
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [nestedValue, setNestedValue] = useState([]);
  const { isTabbing } = useWithA11y();

  useEffect(() => {
    setNestedValue([...aggregateValue]);
  }, [aggregateValue]);

  const handleNestedValue = (e) => {
    let newAggregateValues = [...aggregateValue.toggleValues(e.target?.value)];
    setNestedValue(newAggregateValues);
    onClick({
      target: {
        value: newAggregateValues,
        name: name,
        label: label,
      },
    });
  };

  const handleActiveKeypress = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (KeyCodes.POSITIVE_AFFIRMATIONS.includes(e.which)) {
      setCollapsed(!collapsed);
    }
  };

  return (
    <StyledCollapsableRow>
      <StyledParent
        label={label}
        collapsed={collapsed}
        value={value}
        isTabbing={isTabbing}
        onClick={() => setCollapsed(!collapsed)}
        onKeyPress={handleActiveKeypress}
        aria-label={`Hit the space bar or enter key to expand options for the ${label} group`}
        tabIndex={0}
      >
        <strong>{label}</strong>
        <CarrotToggle active={!collapsed} />
      </StyledParent>
      <Collapsable collapsed={collapsed} className="collapsable">
        <StyledDropdownContent key={label} collapsed={collapsed}>
          {nestedOptions.map((option) => (
            <MultiSelectOption
              key={option.value}
              label={option.value}
              name={name}
              active={aggregateValue.includes(option.key)}
              value={option.key}
              onClick={handleNestedValue}
              onKeyPress={option.onKeyPress}
              isTabbing={isTabbing}
              isNestedChild
            />
          ))}
        </StyledDropdownContent>
      </Collapsable>
    </StyledCollapsableRow>
  );
};

NestedMultiSelectOption.propTypes = {
  active: bool,
  label: string,
  onClick: func,
  onKeyPress: func,
  handleChange: func,
  value: string,
  nestedOptions: array,
  aggregateValue: array,
  name: string,
};

NestedMultiSelectOption.defaultProps = {
  aggregateValue: [],
};

NestedMultiSelectOption.displayName = 'NestedMultiSelectOption';

export default memo(NestedMultiSelectOption);
